import { useApi } from '@/components/Functional/hooks-core/useApi'
import { ShareSnsCard } from '@/components/Models/share-sns/ShareSnsCard'
import { Button } from '@/components/UI/Button'
import { Skeleton } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

type MessageModalProps = {
  onProceed: () => void
  show: boolean
  error?: string
  /** @deprecated 拡散用SNS（#3285）に置換。API 駆動の ShareSnsCard を使用する */
  hashtag?: string
  /** 拡散用SNS（context=ar_photo）取得に使うスポットID */
  spotId?: number
}

export const MessageModal = ({ show, error, onProceed, spotId }: MessageModalProps) => {
  const { t } = useTranslation()
  const api = useApi()

  // 撮影後画面表示時に ARフォトの拡散用SNS情報を取得（保存成功時のみ）
  const { data: shareSns, isLoading: shareSnsLoading } = useQuery({
    queryKey: ['spotShareSns', spotId, 'ar_photo'],
    queryFn: () => api.guest.fetchSpotShareSns(spotId as number, { context: 'ar_photo' }),
    enabled: show && !!spotId && !error,
  })

  if (!show) return null

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-y-auto bg-white">
      <div className="flex-1 px-4 pt-6 pb-4">
        {/* タイトル */}
        <h2 className="mb-6 text-center text-[18px] font-bold text-black">
          {error ? t('spot_detail.ar.save_error', '保存に失敗しました') : t('spot_detail.ar.saved', '保存しました')}
        </h2>

        {/* 説明テキスト */}
        <p className="mb-6 text-[15px] leading-relaxed text-black">
          {error
            ? error
            : t(
                'spot_detail.ar.save_success_massage_main',
                '保存した写真、動画はお使いのスマートフォンに設定した保存先からご覧になれます。',
              )}
        </p>
        {!error && (
          <p className="mb-6 text-[13px] leading-relaxed text-[#757575]">
            {t(
              'spot_detail.ar.save_success_massage_sub',
              '(iPhoneの場合は「ファイル」アプリ、Androidの場合は「ファイル管理アプリ」に保存されます)',
            )}
          </p>
        )}

        {/* 拡散用SNSカード（#3285・context=ar_photo。保存成功かつ有効時のみ表示。取得中はスケルトン） */}
        {!error && spotId != null && (
          <>
            {shareSnsLoading && (
              <div className="mt-4 mb-4 rounded-[8px] border border-[#FFC500] p-4">
                <div className="mb-4 flex flex-col items-center gap-2">
                  <Skeleton height={18} width={180} radius="sm" />
                  <Skeleton height={18} width={140} radius="sm" />
                </div>
                <div className="flex flex-col items-center gap-3">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} height={36} width={265} radius={8} />
                  ))}
                </div>
              </div>
            )}
            {shareSns && <ShareSnsCard shareSns={shareSns} spotId={spotId} context="ar_photo" />}
          </>
        )}
      </div>

      {/* 閉じるボタン */}
      <div className="flex flex-shrink-0 justify-center px-4 pb-6">
        <Button label={t('common.close', '閉じる')} variant="primary" size="md" onClick={onProceed} fullWidth />
      </div>
    </div>
  )
}
